import { Link } from "react-router-dom";
import { Section, Eyebrow } from "@/components/Section";
import { ArrowUpRight, Brain, HeartHandshake, Shield, Sparkles, Users, Network, Scale, Globe2, LifeBuoy, PenLine } from "lucide-react";

const SERVICES = [
  {
    id: "emdr",
    icon: Brain,
    label: "EMDR",
    title: "Eye Movement Desensitization & Reprocessing",
    body:
      "An evidence-based therapy for trauma. EMDR helps the brain reprocess memories that have become stuck — so they stop hijacking the present. Sessions are paced for your nervous system, with extensive groundwork before we approach the harder material.",
    suited: ["PTSD & complex trauma", "Single-incident trauma", "Performance-related trauma"],
  },
  {
    id: "tf-cbt",
    icon: HeartHandshake,
    label: "TF-CBT",
    title: "Trauma-Focused CBT",
    body:
      "Structured trauma care designed for adolescents and young adults. We build coping skills first, then approach the narrative slowly — with caregiver involvement when it helps and privacy when it's needed.",
    suited: ["Adolescents (10–17)", "Acute or developmental trauma", "Family-collaborative work"],
  },
  {
    id: "cbt",
    icon: Sparkles,
    label: "CBT",
    title: "Cognitive Behavioral Therapy",
    body:
      "Practical, structured therapy for the everyday cost of anxiety and depression. We get curious about the thoughts that fire automatically, test them against reality, and build patterns that hold up under pressure.",
    suited: ["Anxiety & panic", "Depression", "Intrusive thinking"],
  },
  {
    id: "dbt",
    icon: Shield,
    label: "DBT",
    title: "Dialectical Behavior Therapy",
    body:
      "Skills-based therapy for emotional regulation, distress tolerance, and interpersonal effectiveness. Especially useful when feelings feel oversized or relationships feel volatile.",
    suited: ["Emotion dysregulation", "Crisis tolerance", "Boundary work"],
  },
  {
    id: "mst",
    icon: Network,
    label: "MST",
    title: "Multi-Systemic Therapy",
    body:
      "An evidence-based, family-and-community-centered approach for adolescents whose challenges show up across multiple systems — home, school, peers, justice involvement. The work happens where life happens, not just in the therapy room.",
    suited: ["Justice-involved youth", "Child welfare involvement", "Family & systems work"],
  },
  {
    id: "wet",
    icon: PenLine,
    label: "WET",
    title: "Written Exposure Therapy",
    body:
      "A brief, evidence-based protocol for PTSD — typically five sessions of structured writing about a traumatic event. Lower demands than longer exposure therapies, with comparable outcomes for many clients who want shorter, structured care.",
    suited: ["PTSD", "Single-incident trauma", "Clients seeking brief care"],
  },
];

const POPULATIONS = [
  {
    icon: Shield,
    title: "Veterans",
    body: "Active-duty transitions, post-deployment trauma, moral injury, and the quiet grief that takes years to name. No translation required.",
  },
  {
    icon: HeartHandshake,
    title: "LGBTQ+ Clients",
    body: "Affirming care as the floor, not the ceiling. Identity is yours to explore — or to set down — as you choose.",
  },
  {
    icon: Scale,
    title: "Justice-Involved Youth",
    body: "Adolescents navigating the juvenile justice or child welfare system. Court-involved, in placement, or transitioning home — care that meets you where you are.",
  },
  {
    icon: Globe2,
    title: "Immigrants",
    body: "First- and second-generation immigrants navigating life in Colorado. A space that honors language, culture, and the weight of in-between.",
  },
  {
    icon: LifeBuoy,
    title: "Survivors",
    body: "For survivors of assault, abuse, or domestic violence. The pace stays yours — no pressure to share more than you're ready to share. Trauma-informed at every step.",
  },
  {
    icon: Users,
    title: "Ages 10 – 50",
    body: "Adolescents, young adults, and adults. Each developmental moment gets care designed for it — not retrofitted from somewhere else.",
  },
];

export default function Services() {
  return (
    <>
      <Section testId="services-hero" className="!pt-24 !pb-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <Eyebrow>Services</Eyebrow>
            <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl mt-6 text-[#2E3630] leading-[1.02]">
              Different wounds need different kinds of care.
            </h1>
            <p className="font-body mt-8 text-lg text-[#5F6B62] max-w-2xl leading-relaxed">
              Four primary modalities — chosen for each person, not prescribed
              by default. We'll decide together which approach (or combination)
              fits where you are.
            </p>
          </div>
        </div>
      </Section>

      <Section testId="services-list" className="!pt-10">
        <div className="space-y-10">
          {SERVICES.map(({ id, icon: Icon, label, title, body, suited }, i) => (
            <article
              key={id}
              id={id}
              data-testid={`service-${id}`}
              className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-start border-t border-[#E8E5DF] pt-10"
            >
              <div className="lg:col-span-3">
                <div className="flex items-center gap-4">
                  <span className="h-12 w-12 inline-flex items-center justify-center rounded-full bg-[#F0EDE6] text-[#6B8E7B]">
                    <Icon size={22} />
                  </span>
                  <Eyebrow>{label}</Eyebrow>
                </div>
                <p className="font-body mt-6 text-xs text-[#9AA39C]">0{i + 1} / 0{SERVICES.length}</p>
              </div>
              <div className="lg:col-span-6">
                <h2 className="font-serif-display text-3xl sm:text-4xl text-[#2E3630] leading-tight">{title}</h2>
                <p className="font-body mt-5 text-[#5F6B62] leading-relaxed text-lg">{body}</p>
              </div>
              <div className="lg:col-span-3">
                <p className="eyebrow text-[10px] text-[#6B8E7B]">Best suited for</p>
                <ul className="mt-4 space-y-2 font-body text-sm text-[#2E3630]">
                  {suited.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <span className="mt-2 h-1 w-1 rounded-full bg-[#6B8E7B]" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </Section>

      {/* Populations */}
      <section className="relative py-24" data-testid="populations-section" style={{ background: "#F0EDE6" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-12">
            <div className="lg:col-span-7">
              <Eyebrow>Who I work with</Eyebrow>
              <h2 className="font-serif-display text-3xl sm:text-5xl mt-5 text-[#2E3630] leading-[1.05]">
                A practice shaped by the people in the room.
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {POPULATIONS.map(({ icon: Icon, title, body }) => (
              <div key={title} data-testid={`population-${title.toLowerCase().replace(/[^a-z]/g, "")}`} className="bg-white rounded-[2rem] p-8 border border-[#E8E5DF]">
                <span className="h-11 w-11 inline-flex items-center justify-center rounded-full bg-[#F9F7F4] text-[#6B8E7B]">
                  <Icon size={20} />
                </span>
                <h3 className="font-serif-display text-2xl mt-6 text-[#2E3630]">{title}</h3>
                <p className="font-body text-[#5F6B62] mt-3 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Section testId="services-rates" className="!pt-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Eyebrow>Rates</Eyebrow>
            <h2 className="font-serif-display text-3xl sm:text-5xl mt-5 text-[#2E3630] leading-[1.05]">
              Private pay, transparent pricing.
            </h2>
            <p className="font-body mt-6 text-[#5F6B62] leading-relaxed">
              No surprises. Sessions are private-pay; superbills are available on request for out-of-network reimbursement.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div data-testid="rate-hour" className="bg-[#2E3630] text-white rounded-[2rem] p-10 sm:p-12 max-w-md">
              <p className="eyebrow text-[#6B8E7B]">60 minutes</p>
              <p className="font-serif-display text-6xl sm:text-7xl mt-4">$175</p>
              <p className="font-body text-sm text-[#C4B7A5] mt-2">per session</p>
              <p className="font-body mt-6 text-[#C4B7A5] leading-relaxed text-sm">
                Standard hour session — suitable for EMDR, CBT, DBT, MST, WET, and ongoing therapeutic work.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section testId="services-insurance" className="!pt-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Eyebrow>Insurance</Eyebrow>
            <h2 className="font-serif-display text-3xl sm:text-5xl mt-5 text-[#2E3630] leading-[1.05]">
              In-network with select plans.
            </h2>
            <p className="font-body mt-6 text-[#5F6B62] leading-relaxed">
              Sessions are billed through two platforms — Headway and
              SonderMind. If your plan isn't listed, private-pay is available
              and superbills can be provided for out-of-network reimbursement.
            </p>
            <p className="font-body text-xs text-[#9AA39C] mt-5">
              Please verify coverage with your insurer before your first session.
            </p>
          </div>

          <div className="lg:col-span-7 space-y-14">
            {/* Headway */}
            <div data-testid="insurance-headway">
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <h3 className="font-serif-display text-2xl sm:text-3xl text-[#2E3630]">Through Headway</h3>
                <a
                  href="https://care.headway.co/providers/jeremy-fuerte?utm_source=pem&utm_medium=direct_link&utm_campaign=221834"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="services-headway-link"
                  className="text-sm font-body text-[#6B8E7B] hover:text-[#557564] inline-flex items-center gap-1.5"
                >
                  View Headway profile <ArrowUpRight size={14} />
                </a>
              </div>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2.5">
                {[
                  "Select Health Colorado",
                  "Carelon Behavioral Health",
                  "Anthem Blue Cross Blue Shield Colorado",
                  "Cigna",
                  "Aetna",
                  "Independence Blue Cross (Pennsylvania)",
                  "Horizon Blue Cross Blue Shield of New Jersey",
                ].map((name) => (
                  <li
                    key={name}
                    data-testid={`insurance-headway-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="flex items-start gap-3 bg-white border border-[#E8E5DF] rounded-2xl px-4 py-3"
                  >
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#6B8E7B] shrink-0" />
                    <span className="font-body text-sm text-[#2E3630] leading-snug">{name}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* SonderMind */}
            <div data-testid="insurance-sondermind">
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <h3 className="font-serif-display text-2xl sm:text-3xl text-[#2E3630]">Through SonderMind</h3>
                <a
                  href="https://www.sondermind.com/providers/ze4ixs09/jeremy-fuerte?utm_source=shared"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="services-sondermind-link"
                  className="text-sm font-body text-[#6B8E7B] hover:text-[#557564] inline-flex items-center gap-1.5"
                >
                  View SonderMind profile <ArrowUpRight size={14} />
                </a>
              </div>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2.5">
                {[
                  "Aetna Allied Benefit System",
                  "Aetna AmeriBen",
                  "Aetna CoreSource",
                  "Aetna EBMS",
                  "Aetna Gravie",
                  "Aetna Marpai",
                  "Aetna Meritain",
                  "Aetna WebTPA",
                  "Alliance Coal Health Plan",
                  "Anthem Blue Cross Blue Shield — Pathway",
                  "Anthem Blue Cross Blue Shield (HMO)",
                  "Anthem Blue Cross Blue Shield (PPO)",
                  "Centivo Ellwood Group",
                  "Cigna",
                  "Cigna A2",
                  "Cigna HealthPartners",
                  "Cigna EAP",
                  "ClaimDOC",
                  "Denver Health Medical Plan",
                  "FAIROS powered by OccuNet",
                  "First Choice Health",
                  "Headspace EAP/Care",
                  "Lucent Health — Summit County Government Plan",
                  "Medical Mutual",
                  "Partners Direct",
                  "Sana Benefits",
                  "Select Health",
                  "Simplified Benefits Administrators",
                  "UMR Monument Health Plan",
                  "Unity Preferred Network",
                  "Unity Preferred Workers Comp",
                  "Velocity National Provider Network",
                  "Velocity National Provider Network Workers Comp",
                ].map((name) => (
                  <li
                    key={name}
                    data-testid={`insurance-sondermind-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="flex items-start gap-3 bg-white border border-[#E8E5DF] rounded-2xl px-4 py-3"
                  >
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#6B8E7B] shrink-0" />
                    <span className="font-body text-sm text-[#2E3630] leading-snug">{name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      <Section testId="services-cta">
        <div className="rounded-[2rem] border border-[#E8E5DF] bg-white p-10 sm:p-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <Eyebrow>Not sure where to start?</Eyebrow>
            <h2 className="font-serif-display text-3xl sm:text-5xl mt-5 text-[#2E3630] leading-tight">
              Begin with a free 15-minute consultation.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/contact" data-testid="services-cta-book" className="btn-pill btn-primary">
              Get in touch <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </Section>
    </>
  );
}
